<template>
  <div class="campaign-form-page">
    <Head title="Edit Campaign" />

    <div class="mb-6">
      <nav class="mb-2 flex items-center gap-2 text-sm text-gray-500">
        <Link href="/campaign" class="font-medium text-brand-600 hover:text-brand-800">Campaign</Link>
        <span>/</span>
        <span class="text-gray-700">Edit</span>
      </nav>
      <p class="text-xs font-semibold uppercase tracking-[0.14em] text-brand-500">Operations</p>
      <h1 class="mt-1 text-3xl font-bold tracking-tight text-brand-900">Edit Campaign</h1>
      <p class="mt-1.5 text-sm text-gray-500">Update campaign details, channel mapping, and status.</p>
    </div>

    <div class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div
        v-if="successMessage"
        class="mx-6 mt-5 rounded-xl border border-teal-200 bg-teal-50 px-4 py-3 text-sm text-teal-800"
      >
        {{ successMessage }}
      </div>

      <form class="p-6 md:p-8" @submit.prevent="update">
        <div class="grid grid-cols-1 gap-5 md:grid-cols-2">
          <text-input v-model="form.title" :error="form.errors.title" class="w-full" label="Title" />
          <text-input v-model="form.name" :error="form.errors.name" class="w-full" label="Full Name" />
          <text-input v-model="form.utm_id" :error="form.errors.utm_id" class="w-full" label="UTM ID" />
          <text-input v-model="form.medium" :error="form.errors.medium" class="w-full" label="Medium" />
          <text-input
            v-model="form.source_link"
            :error="form.errors.source_link"
            class="w-full md:col-span-2"
            label="Source Link"
            readonly
          />
          <p class="-mt-2 text-xs text-gray-500 md:col-span-2">
            Generated automatically from UTM ID, medium, and channel.
          </p>
          <div class="w-full">
            <select-input v-model="form.channel_id" :error="form.errors.channel_id" label="Channel" required>
              <option value="" disabled>Select a channel</option>
              <option v-for="option in options" :key="option.agtCd" :value="option.agtCd">{{ option.name }}</option>
            </select-input>
          </div>

          <div class="flex flex-wrap items-center gap-6 rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 md:col-span-2">
            <div class="flex items-center gap-3">
              <Toggle
                v-model="form.value"
                :classes="{
                  container: 'inline-block rounded-full outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2',
                  toggle: 'flex w-11 h-6 rounded-full relative cursor-pointer transition items-center border-2',
                  toggleOn: 'bg-brand-600 border-brand-600 justify-start text-white',
                  toggleOff: 'bg-gray-200 border-gray-200 justify-end text-gray-600',
                }"
              />
              <label class="text-sm font-semibold text-gray-700">Status (Active)</label>
            </div>
            <div class="flex items-center gap-3">
              <Toggle
                v-model="form.is_agent"
                :classes="{
                  container: 'inline-block rounded-full outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2',
                  toggle: 'flex w-11 h-6 rounded-full relative cursor-pointer transition items-center border-2',
                  toggleOn: 'bg-brand-600 border-brand-600 justify-start text-white',
                  toggleOff: 'bg-gray-200 border-gray-200 justify-end text-gray-600',
                }"
              />
              <label class="text-sm font-semibold text-gray-700">Is Agent</label>
            </div>
          </div>
        </div>

        <div class="mt-8 flex items-center justify-end gap-3 border-t border-gray-100 pt-6">
          <Link
            href="/campaign"
            class="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </Link>
          <loading-button
            :loading="form.processing"
            class="inline-flex items-center justify-center rounded-xl bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-700"
            type="submit"
          >
            Update Campaign
          </loading-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'
import Toggle from '@vueform/toggle'

export default {
  components: {
    Head,
    Link,
    LoadingButton,
    SelectInput,
    TextInput,
    Toggle,
  },
  layout: Layout,
  remember: 'form',
  props: {
    campaign: {
      type: Object,
      required: true,
    },
    options: Array,
    successMessage: String,
  },
  data() {
    return {
      form: this.$inertia.form({
        _method: 'put',
        utm_id: this.campaign.utm_id,
        title: this.campaign.title,
        name: this.campaign.name,
        source_link: this.campaign.source_link,
        medium: this.campaign.medium,
        channel_id: this.campaign.channel_id,
        value: this.campaign.status === 'Active',
        is_agent: this.campaign.is_agent === 'Y',
      }),
    }
  },
  watch: {
    'form.utm_id': 'syncSourceLink',
    'form.medium': 'syncSourceLink',
    'form.channel_id': 'syncSourceLink',
  },
  methods: {
    normalizeSourcePart(value) {
      return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
    },
    selectedChannelName() {
      const channel = (this.options || []).find((option) => option.agtCd === this.form.channel_id)
      return channel?.name || ''
    },
    buildSourceLink() {
      const utmId = String(this.form.utm_id || '').trim()
      const medium = this.normalizeSourcePart(this.form.medium)
      const channelName = this.normalizeSourcePart(this.selectedChannelName())

      if (!utmId || !medium || !channelName) return ''

      const params = new URLSearchParams({
        utm_source: channelName,
        utm_medium: medium,
        utm_campaign: this.normalizeSourcePart(utmId),
        utm_id: utmId,
      })

      return `https://kta.okbank.co.id/id?${params.toString()}`
    },
    syncSourceLink() {
      this.form.source_link = this.buildSourceLink()
    },
    update() {
      this.syncSourceLink()
      this.form.post(`/campaign/${this.campaign.utm_id}`)
    },
  },
  mounted() {
    this.syncSourceLink()
  },
}
</script>
<style src="@vueform/toggle/themes/default.css"></style>
